import React, { useState } from 'react';
import { PdfUpload, Logo, EmailDashboard, ManualInvoiceCreate } from '@components';
import { UploadResponse, InvoiceData, DuplicateDetails } from '@types';
import {
  CheckCircle,
  FileText,
  DollarSign,
  Calendar,
  Building,
  Sparkles,
  Shield,
  Clock,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import {
  transformBackendDuplicateResponse,
  BackendUploadResponse,
} from '@utils/duplicateResponseTransformer';
import { logWarn, logError } from '@utils/logger';
import { Decimal } from 'decimal.js';
import DOMPurify from 'dompurify';

// Type alias for better expressiveness and type safety
type InvoiceDataInput = InvoiceData | readonly InvoiceData[];

// Type guard for better type narrowing
const isMultipleInvoices = (data: InvoiceDataInput): data is readonly InvoiceData[] => {
  return Array.isArray(data);
};

export const HomePage: React.FC = () => {
  const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null);
  const [expandedInvoices, setExpandedInvoices] = useState<Set<number>>(new Set());
  const [uploadMode, setUploadMode] = useState<'automated' | 'manual'>('automated'); // Default to Automated (OCR)

  const handleUploadSuccess = (response: BackendUploadResponse) => {
    // Transform backend response to match DuplicateWarning component expectations
    const transformedResponse = transformBackendDuplicateResponse(response);
    setUploadResult(transformedResponse);
  };

  const toggleInvoiceExpansion = React.useCallback((invoiceIndex: number) => {
    setExpandedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceIndex)) {
        newSet.delete(invoiceIndex);
      } else {
        newSet.add(invoiceIndex);
      }
      return newSet;
    });
  }, []);

  // Input validation for duplicate details
  const isValidDuplicateDetails = (details: unknown): details is DuplicateDetails => {
    if (!details || typeof details !== 'object' || details === null) return false;

    const obj = details as Record<string, unknown>;
    return (
      'summary' in obj &&
      'skippedInvoices' in obj &&
      'createdDuplicates' in obj &&
      'recommendations' in obj
    );
  };

  // Data sanitization for security using DOMPurify
  const sanitizeText = (text: string): string => {
    if (typeof text !== 'string') return '';

    // Use DOMPurify for comprehensive XSS protection
    // Configure to allow only safe text content
    const cleanText = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: [], // No HTML tags allowed
      ALLOWED_ATTR: [], // No attributes allowed
      KEEP_CONTENT: true, // Keep text content
      RETURN_DOM: false, // Return string
      RETURN_DOM_FRAGMENT: false, // Return string
      RETURN_TRUSTED_TYPE: false, // Return string
    });

    return cleanText;
  };

  // Properly memoized currency formatter
  const currencyFormatter = React.useMemo(() => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  const formatCurrency = React.useCallback(
    (amount: string | number): string => {
      try {
        // Use Decimal.js for precise financial calculations
        const decimalAmount = new Decimal(amount);

        if (!decimalAmount.isFinite()) {
          logWarn('Invalid currency amount', 'CurrencyFormatter', {
            amount,
            parsedValue: decimalAmount.toString(),
          });
          return '$0.00';
        }

        // Format with proper decimal precision
        const formattedAmount = decimalAmount.toFixed(2);
        return currencyFormatter.format(parseFloat(formattedAmount)).replace('A$', '$');
      } catch (error) {
        logError('Error formatting currency', 'CurrencyFormatter', {
          amount,
          error: error instanceof Error ? error.message : String(error),
        });
        return '$0.00';
      }
    },
    [currencyFormatter]
  );

  // Performance optimization: Pre-compute lookup maps for O(1) access
  const skippedInvoicesMap = React.useMemo(() => {
    if (!uploadResult?.duplicateDetails?.skippedInvoices)
      return new Map<string, DuplicateDetails['skippedInvoices'][0]>();
    return new Map(
      uploadResult.duplicateDetails.skippedInvoices.map(
        (skipped: DuplicateDetails['skippedInvoices'][0]) => [skipped.invoiceNumber, skipped]
      )
    );
  }, [uploadResult?.duplicateDetails?.skippedInvoices]);

  const renderInvoiceData = (data: InvoiceDataInput): JSX.Element => {
    if (isMultipleInvoices(data)) {
      return (
        <div className="space-y-4">
          <div className="flex items-center space-x-2 mb-4">
            <Sparkles className="w-5 h-5 text-accent-500" />
            <h3 className="text-lg font-semibold text-secondary-800">
              Multiple Invoices Detected ({data.length})
            </h3>
          </div>
          {/* Single column layout for maximum space usage */}
          <div className="grid grid-cols-1 gap-6">
            {data.map((invoice, index) => (
              <div key={`invoice-${index}`} className="card-hover">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Building className="w-4 h-4 text-secondary-500" />
                      <span className="text-sm text-secondary-500">Supplier</span>
                    </div>
                    <p className="font-medium text-secondary-800">{invoice.supplier}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-secondary-500" />
                      <span className="text-sm text-secondary-500">Invoice Number</span>
                    </div>
                    <p className="font-medium text-secondary-800">{invoice.invoice_number}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-secondary-500" />
                      <span className="text-sm text-secondary-500">Date</span>
                    </div>
                    <p className="font-medium text-secondary-800">{invoice.invoice_date}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="w-4 h-4 text-secondary-500" />
                      <span className="text-sm text-secondary-500">Amount</span>
                    </div>
                    <p className="font-semibold text-success-600">
                      {formatCurrency(invoice.total_amount)}
                    </p>
                  </div>
                </div>

                {invoice.supplier_abn && (
                  <div className="mt-4 pt-4 border-t border-secondary-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-secondary-500">ABN:</span>
                        <span className="font-medium text-secondary-700">
                          {invoice.supplier_abn}
                        </span>
                      </div>

                      {/* Duplicate Detection integrated in ABN row */}
                      {uploadResult?.duplicateDetails &&
                        isValidDuplicateDetails(uploadResult.duplicateDetails) && (
                          <button
                            onClick={() => toggleInvoiceExpansion(index)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleInvoiceExpansion(index);
                              }
                            }}
                            className="flex items-center space-x-2 px-3 py-1 rounded transition-colors hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                            aria-expanded={expandedInvoices.has(index)}
                            aria-controls={`duplicate-details-${index}`}
                            aria-label={`Toggle duplicate details for invoice ${sanitizeText(invoice.invoice_number)}`}
                            role="button"
                            tabIndex={0}
                          >
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <span className="text-sm font-medium text-amber-700">
                              Duplicate Detected
                            </span>
                            {expandedInvoices.has(index) ? (
                              <ChevronDown className="w-4 h-4 text-amber-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-amber-600" />
                            )}
                          </button>
                        )}
                    </div>

                    {/* Expanded duplicate details below ABN row */}
                    {uploadResult?.duplicateDetails &&
                      isValidDuplicateDetails(uploadResult.duplicateDetails) &&
                      expandedInvoices.has(index) && (
                        <div
                          id={`duplicate-details-${index}`}
                          className="mt-3 p-3 bg-warning-50 border border-warning-200 rounded"
                          role="region"
                          aria-label="Duplicate details"
                        >
                          <div className="space-y-2 text-sm">
                            <p className="text-warning-800">
                              This invoice matches an existing record in the system.
                            </p>
                            {(() => {
                              const skippedInvoice = skippedInvoicesMap.get(invoice.invoice_number);
                              return (
                                skippedInvoice && (
                                  <div className="text-warning-700">
                                    <p>
                                      <strong>Status:</strong> Skipped as duplicate
                                    </p>
                                    <p>
                                      <strong>Reason:</strong> {sanitizeText(skippedInvoice.reason)}
                                    </p>
                                    {skippedInvoice.originalInvoice && (
                                      <p>
                                        <strong>Match Type:</strong>{' '}
                                        {skippedInvoice.originalInvoice.matchType}
                                      </p>
                                    )}
                                  </div>
                                )
                              );
                            })()}
                          </div>
                        </div>
                      )}
                  </div>
                )}

                {/* Error state for invalid duplicate data */}
                {uploadResult?.duplicateDetails &&
                  !isValidDuplicateDetails(uploadResult.duplicateDetails) && (
                    <div className="mt-4 pt-4 border-t border-error-200">
                      <div className="flex items-center space-x-2 p-2">
                        <AlertTriangle className="w-4 h-4 text-error-600" />
                        <span className="text-sm text-error-700">
                          Error: Invalid duplicate data format
                        </span>
                      </div>
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      );
    } else {
      return (
        <div className="card-hover">
          <div className="flex items-center space-x-2 mb-4">
            <Sparkles className="w-5 h-5 text-accent-500" />
            <h3 className="text-lg font-semibold text-secondary-800">Single Invoice Detected</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Building className="w-4 h-4 text-secondary-500" />
                  <span className="text-sm text-secondary-500">Supplier</span>
                </div>
                <p className="font-medium text-lg text-secondary-800">{data.supplier}</p>
              </div>

              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <FileText className="w-4 h-4 text-secondary-500" />
                  <span className="text-sm text-secondary-500">Invoice Number</span>
                </div>
                <p className="font-medium text-secondary-800">{data.invoice_number}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-4 h-4 text-secondary-500" />
                  <span className="text-sm text-secondary-500">Date</span>
                </div>
                <p className="font-medium text-secondary-800">{data.invoice_date}</p>
              </div>

              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="w-4 h-4 text-secondary-500" />
                  <span className="text-sm text-secondary-500">Amount</span>
                </div>
                <p className="font-semibold text-2xl text-success-600">
                  {formatCurrency(data.total_amount)}
                </p>
              </div>
            </div>
          </div>

          {data.supplier_abn && (
            <div className="mt-6 pt-4 border-t border-secondary-200">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-secondary-500">ABN:</span>
                <span className="font-medium text-secondary-700">{data.supplier_abn}</span>
              </div>
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="mb-6">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="bg-primary-500 p-6 rounded-lg shadow-lg">
              <Logo size="lg" />
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-secondary-800 mb-4">
            Invoice Processor
          </h1>
          <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
            Transform your PDF invoices into structured data with AI-powered extraction. Fast,
            accurate, and secure processing for your business needs.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card text-center hover-lift">
            <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-6 h-6 text-accent-600" />
            </div>
            <h3 className="font-semibold text-secondary-800 mb-2">AI-Powered</h3>
            <p className="text-sm text-secondary-600">
              Advanced AI extracts key information automatically
            </p>
          </div>

          <div className="card text-center hover-lift">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Shield className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="font-semibold text-secondary-800 mb-2">Secure</h3>
            <p className="text-sm text-secondary-600">
              Your data is protected with enterprise-grade security
            </p>
          </div>

          <div className="card text-center hover-lift">
            <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Clock className="w-6 h-6 text-accent-600" />
            </div>
            <h3 className="font-semibold text-secondary-800 mb-2">Fast</h3>
            <p className="text-sm text-secondary-600">Process invoices in seconds, not minutes</p>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {/* Upload Section - Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column - Title and Toggle */}
          <div className="flex flex-col items-center justify-center space-y-4 border-2 border-primary-500 rounded-lg shadow-lg p-6 min-h-[320px]">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-secondary-800 mb-2">Upload PDF Invoice</h2>
              <p className="text-sm text-secondary-600">
                {uploadMode === 'automated'
                  ? 'Drag and drop your PDF invoice or click to browse'
                  : 'Upload your PDF and manually enter invoice details (Single invoice only)'}
              </p>
            </div>

            {/* Toggle buttons - stacked vertically */}
            <div className="flex flex-col items-center space-y-3">
              <button
                type="button"
                onClick={() => setUploadMode('automated')}
                className={`
                  px-5 py-3 rounded-md text-base font-medium transition-all w-[180px]
                  ${
                    uploadMode === 'automated'
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                  }
                `}
              >
                Automated (OCR)
              </button>
              <button
                type="button"
                onClick={() => setUploadMode('manual')}
                className={`
                  px-5 py-3 rounded-md text-base font-medium transition-all w-[180px]
                  ${
                    uploadMode === 'manual'
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                  }
                `}
              >
                Manual
              </button>
            </div>
          </div>

          {/* Right Column - Upload Component */}
          <div className="w-full">
            {uploadMode === 'automated' ? (
              <PdfUpload onUploadSuccess={handleUploadSuccess} />
            ) : (
              <ManualInvoiceCreate />
            )}
          </div>
        </div>

        {/* Email Processing Section */}
        <EmailDashboard logsLimit={5} showRetryButton={true} />

        {/* Results Section */}
        {uploadResult && (
          <div className="animate-fade-in">
            <div className="flex items-center space-x-2 mb-6">
              <CheckCircle className="w-6 h-6 text-success-500" />
              <h2 className="text-2xl font-semibold text-secondary-800">Processing Results</h2>
            </div>

            {/* Invoice Data with integrated duplicate detection */}
            {renderInvoiceData(uploadResult.originalData)}

            {/* Success Summary */}
            <div className="mt-8 card bg-primary-50 border-primary-200">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle className="w-5 h-5 text-primary-600" />
                <span className="font-medium text-primary-800">Processing Summary</span>
              </div>
              <p className="text-primary-700">{uploadResult.processingSummary.message}</p>
              {uploadResult.originalFilename && uploadResult.individualPdfFilenames && (
                <div className="mt-3 pt-3 border-t border-primary-200">
                  <p className="text-sm text-primary-600">
                    <span className="font-medium">Original filename:</span>{' '}
                    {uploadResult.originalFilename}
                  </p>
                  {uploadResult.individualPdfFilenames.length === 1 ? (
                    <p className="text-sm text-primary-600">
                      <span className="font-medium">Individual PDF:</span>{' '}
                      {uploadResult.individualPdfFilenames[0]}
                    </p>
                  ) : (
                    <div>
                      <p className="text-sm text-primary-600 font-medium">Individual PDFs:</p>
                      {uploadResult.individualPdfFilenames.map(
                        (filename: string, index: number) => (
                          <p key={index} className="text-sm text-primary-600 ml-4">
                            • {filename}
                          </p>
                        )
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
